import { useCallback, useMemo, useRef, useState } from 'react';
import { ApiError } from '../lib/http/ApiError';
import type { ApiErrorPayload, FetchClient, RequestOptions } from '../lib/http/http.types';
import { useAuth } from '../features/auth/hooks/useAuth';
import { DEFAULT_TENANT } from '../shared/constants';

const BASE_URL = `${window.__env__?.VITE_BACKEND_HOMERE_CONNECT_DOMAIN ?? '/api'}`;

async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  if (!response.ok) {
    const fallback: ApiErrorPayload = {
      message: text || response.statusText,
      statusCode: response.status,
    };
    const payload: ApiErrorPayload = (() => {
      try {
        const parsed: unknown = JSON.parse(text);
        // JSON valide mais pas un objet (null, nombre, tableau…) : traité comme un corps non-JSON.
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
          ? (parsed as ApiErrorPayload)
          : fallback;
      } catch {
        return fallback;
      }
    })();
    throw new ApiError(payload, response.status);
  }

  if (!text) {
    // handle empty response as undefined (e.g. DELETE) — avoids JSON.parse error
    return undefined as unknown as T;
  }
  return JSON.parse(text) as T;
}

/**
 * Hook HTTP authentifié — l'API wrapper central de l'application.
 *
 * Lit le token depuis AuthContext à chaque requête.
 * Sur HTTP 401, termine la session via endSession() (AuthContext) ; sur HTTP 403,
 * resynchronise le rôle et les droits via refreshPermissions().
 * Retourne un tuple [client, isLoading] :
 *   - client : interface FetchClient stable (référence stable tant que le token ne change pas)
 *   - isLoading : true dès qu'au moins une requête est en cours (compteur inflight)
 *
 * Utilisation dans une feature hook :
 *   const [client, loading] = useFetch();
 *   const api = useMemo(() => createUsersApi(client), [client]);
 *
 * Migration TanStack Query (seule la couche hook change) :
 *   const [client] = useFetch();            // client reste identique
 *   return useQuery({ queryFn: ({ signal }) => client.get('/users', { signal }) });
 */

// TODO : Replace tenant-id header with domain-based tenant resolution when backend supports it (e.g. tenant1.homereconnect.fr → tenant-id = 'tenant1'). The tenant-id header is intentional for dev & test environments only and must be removed before going to production.
export function useFetch(): [FetchClient, boolean] {
  const { token, endSession, refreshPermissions } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const inflight = useRef(0);

  const request = useCallback(
    async <T>(
      method: string,
      path: string,
      body?: unknown,
      options?: RequestOptions,
    ): Promise<T> => {
      inflight.current += 1;
      setIsLoading(true);

      try {
        const isFormData = body instanceof FormData;
        const headers = new Headers({
          'Content-Type': 'application/json',
          ...options?.headers,
          'tenant-id': DEFAULT_TENANT,
        });

        // FormData : le navigateur fixe lui-même le Content-Type avec le boundary. `delete`
        // est insensible à la casse, contrairement au spread ci-dessus (HOMCO-1405).
        if (isFormData) {
          headers.delete('Content-Type');
        }

        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }

        const response = await fetch(`${BASE_URL}${path}`, {
          method,
          headers,
          body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
          signal: options?.signal,
        });

        // 401 : session refusée par l'API (compte désactivé, jeton expiré…) — fin de
        // session locale + redirect /login, sans appel /auth/logout (HOMCO-1184).
        // 403 : rôle ou droits peut-être modifiés depuis le chargement (le rôle est
        // relu en base à chaque requête côté API) — on resynchronise /auth/me, de sorte
        // que menus et ProtectedRoute se réévaluent sans rechargement.
        if (response.status === 401) {
          endSession();
        } else if (response.status === 403) {
          void refreshPermissions();
        }

        return await handleResponse<T>(response);
      } finally {
        inflight.current -= 1;
        if (inflight.current === 0) setIsLoading(false);
      }
    },
    [token, endSession, refreshPermissions],
  );

  // Mirrors `request` above for multipart/form-data — no explicit Content-Type (browser sets boundary)
  const requestMultipart = useCallback(
    async <T>(path: string, body: FormData, options?: RequestOptions): Promise<T> => {
      inflight.current += 1;
      setIsLoading(true);

      try {
        const headers = new Headers({
          ...options?.headers,
          'tenant-id': DEFAULT_TENANT,
        });

        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }

        const response = await fetch(`${BASE_URL}${path}`, {
          method: 'POST',
          headers,
          body,
          signal: options?.signal,
        });

        if (response.status === 401) {
          endSession();
        } else if (response.status === 403) {
          void refreshPermissions();
        }

        return await handleResponse<T>(response);
      } finally {
        inflight.current -= 1;
        if (inflight.current === 0) setIsLoading(false);
      }
    },
    [token, endSession, refreshPermissions],
  );

  const client = useMemo(
    () => ({
      get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
      post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
        request<T>('POST', path, body, opts),
      patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
        request<T>('PATCH', path, body, opts),
      put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
        request<T>('PUT', path, body, opts),
      delete: <T = void>(path: string, opts?: RequestOptions) =>
        request<T>('DELETE', path, undefined, opts),
      postMultipart: <T>(path: string, body: FormData, opts?: RequestOptions) =>
        requestMultipart<T>(path, body, opts),
    }),
    [request, requestMultipart],
  );

  return [client, isLoading];
}
